import {
  computed,
  effect,
  inject,
  Injectable,
  signal,
  WritableSignal,
} from '@angular/core';
import { ViewResponseModel } from 'src/app/shared/models';
import { UserGRPCService } from '../../ProxyServices/user.grpc.service';
import { ViewsGRPCService } from '../../ProxyServices/views.grpc.service';
import { Observable } from 'rxjs';

@Injectable()
export class ViewsService {
  public _clientViews = inject(ViewsGRPCService);
  private _clientUser = inject(UserGRPCService);

  views: WritableSignal<ViewResponseModel[]> = signal([]);
  lastActive = computed<ViewResponseModel>(() => {
    const views = structuredClone(this.views());
    if (views.length > 0) {
      const active = this.getLastSeenFromList();
      if (active.InvStatus === null) {
        // initial state if missing
        active.InvStatus = {
          field: 'StatusWeb',
          operator: 'eq',
          value: ['Expected'],
        };
      }
      return active;
    }
    return null;
  });
  active: WritableSignal<ViewResponseModel> = signal(null);
  filterView: WritableSignal<ViewResponseModel> = signal(null);

  constructor() {
    effect(() => {
      const lastActive = this.lastActive();
      this.active.set(structuredClone(lastActive));
      this.filterView.set(structuredClone(lastActive));
    });
  }

  getLastSeenFromList(contractName?: string): ViewResponseModel {
    const views = structuredClone(this.views());
    const distantFuture = new Date(8640000000000000);
    let tmp: ViewResponseModel[];
    if (contractName) {
      tmp = views.filter((x) => x.contractName == contractName);
    } else {
      tmp = views;
    }

    return tmp.sort((a, b) => {
      let dateA = a.lastSeen ? new Date(a.lastSeen) : distantFuture;
      let dateB = b.lastSeen ? new Date(b.lastSeen) : distantFuture;
      return dateB.getTime() - dateA.getTime();
    })[0];
  }

  update(view: ViewResponseModel): void {
    this.active.set(view);
  }
  updateFilterView(view: ViewResponseModel): void {
    this.filterView.set(view);
  }
  updateContract(name: string) {
    this._clientViews.updateLastSeen(name).subscribe({
      next: (view) => {
        const views = structuredClone(this.views());
        const index = views.findIndex((item) => item.name === view.name);
        if (index !== -1) {
          // Update existing view title
          views[index] = {
            ...view,
            isDefault: views[index].isDefault,
          };
        }
        this.active.set(view);
        this.views.set([...views]);
      },
      error: (error) => {
        console.log(error);
      },
    });
  }

  changeView(name: string): Observable<ViewResponseModel> {
    return new Observable<ViewResponseModel>((observer) => {
      this._clientViews.updateLastSeen(name).subscribe({
        next: (view) => {
          if (view.InvStatus === null) {
            // initial state if missing
            view.InvStatus = {
              field: 'StatusWeb',
              operator: 'eq',
              value: ['Expected'],
            };
          }
          const views = structuredClone(this.views());
          const index = views.findIndex((item) => item.name === view.name);
          if (index !== -1) {
            // Update existing view title
            views[index] = {
              ...view,
              isDefault: views[index].isDefault,
            };
          }
          this.views.set([...views]);
          this.active.set(view);
          observer.next(view);
          observer.complete();
        },
        error: (error) => {
          console.log(error);
        },
      });
    });
  }

  get(): void {
    const user = this._clientUser.user();
    this._clientViews.get(user).subscribe({
      next: (views: ViewResponseModel[]) => {
        this.views.set(views);
      },
      error: (error) => {},
      complete: () => {},
    });
  }

  delete(name: string): void {
    this._clientViews.delete(name).subscribe({
      next: () => {
        const views = structuredClone(this.views());
        views.splice(
          views.findIndex((v) => v.name == name),
          1
        );
        this.views.set([...views]);
      },
      error: (error) => {},
      complete: () => {},
    });
  }

  save(clone: ViewResponseModel): void {
    const user = this._clientUser.user();
    clone.userName = user.name;
    this._clientViews.save(clone).subscribe({
      next: (view: ViewResponseModel) => {
        const views = structuredClone(this.views());
        const index = views.findIndex((item) => item.name === view.name);
        if (index !== -1) {
          // Update existing view title
          views[index] = {
            ...view,
            contractName: views[index].contractName,
            contractTitle: views[index].contractTitle,
          };
        } else {
          // Add new view
          views.push(view);
        }
        this.views.set([...views]);
      },
      error: (error) => {},
      complete: () => {},
    });
  }
}
